/**
 * DEX Contract Addresses and Configuration
 * Supports V3 (PancakeSwap, Uniswap) and V2 (DragonSwap, TraderJoe, Uniswap) protocols
 */

export interface DexContracts {
  router: string; // Swap Router (V3 or primary)
  factory: string; // Factory (V3 or primary)
  positionManager?: string; // Only for V3
  quoterV2?: string; // Only for V3
  weth: string; // Wrapped native token
  dexName: 'PancakeSwap' | 'Uniswap' | 'DragonSwap' | 'TraderJoe' | 'Velodrome';
  version: 'V2' | 'V3';
  // V2 support (for networks that have both V2 and V3)
  v2Router?: string; // V2 Router address
  v2Factory?: string; // V2 Factory address
  supportsV2?: boolean; // Does this network also support V2?
  supportsV3?: boolean; // Does this network also support V3?
}

/**
 * DEX contract addresses by chain ID
 */
export const DEX_CONTRACTS: Record<number, DexContracts> = {
  // ========== PancakeSwap V3 Networks ==========
  
  // opBNB Mainnet (Chain ID: 204)
  204: {
    router: '0xB89a6778D1efE7a5b7096757A21b810CC2886fa1',
    factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865',
    positionManager: '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364',
    quoterV2: '0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997',
    weth: '0x4200000000000000000000000000000000000006', // WBNB
    dexName: 'PancakeSwap',
    version: 'V3',
  },
  
  // opBNB Testnet (Chain ID: 5611)
  5611: {
    router: '0xB89a6778D1efE7a5b7096757A21b810CC2886fa1',
    factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865',
    positionManager: '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364',
    quoterV2: '0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997',
    weth: '0x4200000000000000000000000000000000000006', // WBNB
    dexName: 'PancakeSwap',
    version: 'V3',
  },
  
  // BSC Mainnet (Chain ID: 56) - Supports BOTH V2 and V3!
  56: {
    router: '0x13f4EA83D0bd40E75C8222255bc855a974568Dd4', // V3 Router
    factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865', // V3 Factory
    positionManager: '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364',
    quoterV2: '0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997',
    weth: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // WBNB
    // V2 Support
    v2Router: '0x10ED43C718714eb63d5aA57B78B54704E256024E', // PancakeSwap V2 Router
    v2Factory: '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73', // PancakeSwap V2 Factory
    supportsV2: true,
    supportsV3: true,
    dexName: 'PancakeSwap',
    version: 'V3',
  },
  
  // BSC Testnet (Chain ID: 97)
  97: {
    router: '0x9a489505a00cE272eAa5e07Dba6491314CaE3796',
    factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865',
    positionManager: '0x427bF5b37357632377eCbEC9de3626C71A5396c1',
    quoterV2: '0xbC203d7f83677c7ed3F7acEc959963E7F4ECC5C2',
    weth: '0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd', // WBNB
    dexName: 'PancakeSwap',
    version: 'V3',
  },

  // ========== Uniswap V3 Mainnets ==========
  
  // Ethereum Mainnet (Chain ID: 1) - Supports BOTH V2 and V3!
  1: {
    router: '0xE592427A0AEce92De3Edee1F18E0157C05861564', // V3 Router
    factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984', // V3 Factory
    positionManager: '0xC36442b4a4522E871399CD717aBDD847Ab11FE88',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // WETH
    // V2 Support
    v2Router: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D', // Uniswap V2 Router
    v2Factory: '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f', // Uniswap V2 Factory
    supportsV2: true,
    supportsV3: true,
    dexName: 'Uniswap',
    version: 'V3',
  },

  // Arbitrum One (Chain ID: 42161) - Supports BOTH V2 and V3!
  42161: {
    router: '0xE592427A0AEce92De3Edee1F18E0157C05861564', // V3 Router
    factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984', // V3 Factory
    positionManager: '0xC36442b4a4522E871399CD717aBDD847Ab11FE88',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1', // WETH
    // V2 Support
    v2Router: '0x4752ba5dbc23f44d87826276bf6fd6b1c372ad24', // Uniswap V2 Router (Arbitrum)
    v2Factory: '0xf1D7CC64Fb4452F05c498126312eBE29f30Fbcf9', // Uniswap V2 Factory (Arbitrum)
    supportsV2: true,
    supportsV3: true,
    dexName: 'Uniswap',
    version: 'V3',
  },

  // Optimism Mainnet (Chain ID: 10) - Supports BOTH Uniswap V3 and Velodrome V2!
  10: {
    router: '0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45', // Uniswap V3 Router
    factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984', // Uniswap V3 Factory
    positionManager: '0xC36442b4a4522E871399CD717aBDD847Ab11FE88',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0x4200000000000000000000000000000000000006', // WETH
    // Velodrome V2 Support (most popular DEX on Optimism)
    v2Router: '0xa062aE8A9c5e11aaA026fc2670B0D65cCc8B2858', // Velodrome Router
    v2Factory: '0xF1046053aa5682b4F9a81b5481394DA16BE5FF5a', // Velodrome Factory
    supportsV2: true,
    supportsV3: true,
    dexName: 'Uniswap',
    version: 'V3',
  },

  // Polygon Mainnet (Chain ID: 137) - Supports BOTH V2 and V3!
  137: {
    router: '0xE592427A0AEce92De3Edee1F18E0157C05861564', // V3 Router
    factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984', // V3 Factory
    positionManager: '0xC36442b4a4522E871399CD717aBDD847Ab11FE88',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270', // WMATIC
    // V2 Support (QuickSwap - most popular V2 on Polygon)
    v2Router: '0xa5E0829CaCEd8fFDD4De3c43696c57F7D7A678ff', // QuickSwap V2 Router
    v2Factory: '0x5757371414417b8C6CAad45bAeF941aBc7d3Ab32', // QuickSwap V2 Factory
    supportsV2: true,
    supportsV3: true,
    dexName: 'Uniswap',
    version: 'V3',
  },

  // Base Mainnet (Chain ID: 8453)
  8453: {
    router: '0x198EF79F1F515F02dFE9e3115eD9fC07183f02fC',
    factory: '0x33128a8fC17869897dcE68Ed026d694621f6FDfD',
    positionManager: '0x03a520b32C04BF3bEEf7BEb72E919cf822Ed34f1',
    quoterV2: '0x23cF10b1ee3AdfCA73B0eF17C07F7577e7ACd2d7',
    weth: '0x4200000000000000000000000000000000000006', // WETH
    dexName: 'Uniswap',
    version: 'V3',
  },

  // Avalanche C-Chain (Chain ID: 43114) - Supports BOTH Uniswap V3 and Trader Joe V2!
  43114: {
    router: '0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45', // Uniswap V3 Router
    factory: '0x740b1c1de25031C31FF4fC9A62f554A55cdC1baD', // Uniswap V3 Factory
    positionManager: '0x655C406EBFa14EE2006250925e54ec43AD184f8B',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7', // WAVAX
    // Trader Joe V2 Support
    v2Router: '0x60aE616a2155Ee3d9A68541Ba4544862310933d4', // Trader Joe V2 Router (LBRouter)
    v2Factory: '0x8e42f2F4101563bF679975178e880FD87d3eFd4e', // Trader Joe V2 Factory (LBFactory)
    supportsV2: true,
    supportsV3: true,
    dexName: 'Uniswap',
    version: 'V3',
  },

  // ========== Uniswap V3 Testnets ==========

  // Sepolia Testnet (Chain ID: 11155111)
  11155111: {
    router: '0xE592427A0AEce92De3Edee1F18E0157C05861564',
    factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984',
    positionManager: '0xC36442b4a4522E871399CD717aBDD847Ab11FE88',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0xfFf9976782d46CC05630D1f6eBAb18b2324d6B14', // WETH
    dexName: 'Uniswap',
    version: 'V3',
  },

  // Arbitrum Sepolia (Chain ID: 421614)
  421614: {
    router: '0xE592427A0AEce92De3Edee1F18E0157C05861564',
    factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984',
    positionManager: '0xC36442b4a4522E871399CD717aBDD847Ab11FE88',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0x980B62Da83eFf3D4576C647993b0c1D7faf17c73', // WETH
    dexName: 'Uniswap',
    version: 'V3',
  },

  // OP Sepolia (Chain ID: 11155420)
  11155420: {
    router: '0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45',
    factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984',
    positionManager: '0xC36442b4a4522E871399CD717aBDD847Ab11FE88',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0x4200000000000000000000000000000000000006', // WETH
    dexName: 'Uniswap',
    version: 'V3',
  },

  // Base Sepolia (Chain ID: 84532)
  84532: {
    router: '0x198EF79F1F515F02dFE9e3115eD9fC07183f02fC',
    factory: '0x33128a8fC17869897dcE68Ed026d694621f6FDfD',
    positionManager: '0x03a520b32C04BF3bEEf7BEb72E919cf822Ed34f1',
    quoterV2: '0x23cF10b1ee3AdfCA73B0eF17C07F7577e7ACd2d7',
    weth: '0x4200000000000000000000000000000000000006', // WETH
    dexName: 'Uniswap',
    version: 'V3',
  },

  // Polygon Amoy Testnet (Chain ID: 80002)
  80002: {
    router: '0xE592427A0AEce92De3Edee1F18E0157C05861564',
    factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984',
    positionManager: '0xC36442b4a4522E871399CD717aBDD847Ab11FE88',
    quoterV2: '0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6',
    weth: '0x360ad4f9a9A8EFe9A8DCB5f461c4Cc1047E1Dcf9', // WMATIC
    dexName: 'Uniswap',
    version: 'V3',
  },

  // ========== V2 Networks ==========

  // Abstract Mainnet (Chain ID: 2741) - Uniswap V2
  2741: {
    router: '0xad1eCa41E6F772bE3cb5A48A6141f9bcc1AF9F7c',
    factory: '0x179D9a5592Bc77050796F7be28058c51cA575df4', // Using DragonSwap factory as placeholder
    weth: '0x4200000000000000000000000000000000000006', // WETH on Abstract
    dexName: 'Uniswap',
    version: 'V2',
  },

  // Abstract Testnet (Chain ID: 11124) - Uniswap V2
  11124: {
    router: '0x96ff7D9dbf52FdcAe79157d3b249282c7FABd409',
    factory: '0x179D9a5592Bc77050796F7be28058c51cA575df4', // Using DragonSwap factory as placeholder
    weth: '0x4200000000000000000000000000000000000006', // WETH on Abstract
    dexName: 'Uniswap',
    version: 'V2',
  },

  // Avalanche Fuji Testnet (Chain ID: 43113) - Trader Joe V2
  43113: {
    router: '0xd7f655E3376cE2D7A2b08fF01Eb3B1023191A901',
    factory: '0x179D9a5592Bc77050796F7be28058c51cA575df4', // Using DragonSwap factory as placeholder
    weth: '0xd00ae08403B9bbb9124bB305C09058E32C39A48c', // WAVAX on Fuji
    dexName: 'TraderJoe',
    version: 'V2',
  },

  // Sei Mainnet (Chain ID: 1329) - DragonSwap V2
  1329: {
    router: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
    factory: '0x179D9a5592Bc77050796F7be28058c51cA575df4',
    weth: '0xE30feDd158A2e3b13e9badaeABaFc5516e95e8C7', // WSEI
    dexName: 'DragonSwap',
    version: 'V2',
  },

  // Sei Atlantic-2 Testnet (Chain ID: 1328) - DragonSwap V2
  1328: {
    router: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
    factory: '0x179D9a5592Bc77050796F7be28058c51cA575df4',
    weth: '0xE30feDd158A2e3b13e9badaeABaFc5516e95e8C7', // WSEI
    dexName: 'DragonSwap',
    version: 'V2',
  },
};

/**
 * Fee tiers for V3 pools (in basis points)
 */
export const V3_FEE_TIERS = {
  LOWEST: 100,    // 0.01%
  LOW: 500,       // 0.05%
  MEDIUM: 2500,   // 0.25%
  HIGH: 10000,    // 1%
} as const;

/**
 * Get DEX contracts for a specific chain
 */
export function getDexContracts(chainId: number): DexContracts | null {
  return DEX_CONTRACTS[chainId] || null;
}

/**
 * Check if DEX is supported on a chain
 */
export function isDexSupported(chainId: number): boolean {
  return chainId in DEX_CONTRACTS;
}

/**
 * Check if chain supports V3
 */
export function isV3Supported(chainId: number): boolean {
  const contracts = DEX_CONTRACTS[chainId];
  return contracts?.version === 'V3' || contracts?.supportsV3 === true;
}

/**
 * Check if chain supports V2
 */
export function isV2Supported(chainId: number): boolean {
  const contracts = DEX_CONTRACTS[chainId];
  return contracts?.version === 'V2' || contracts?.supportsV2 === true;
}

/**
 * Check if chain supports BOTH V2 and V3
 */
export function supportsDualProtocol(chainId: number): boolean {
  const contracts = DEX_CONTRACTS[chainId];
  return contracts?.supportsV2 === true && contracts?.supportsV3 === true;
}

/**
 * Get V2 contracts for a chain (if supported)
 */
export function getV2Contracts(chainId: number): { router: string; factory: string } | null {
  const contracts = DEX_CONTRACTS[chainId];
  if (!contracts) return null;
  
  // If primary version is V2, use main addresses
  if (contracts.version === 'V2') {
    return {
      router: contracts.router,
      factory: contracts.factory,
    };
  }
  
  // If supports V2 as secondary, use v2 addresses
  if (contracts.v2Router && contracts.v2Factory) {
    return {
      router: contracts.v2Router,
      factory: contracts.v2Factory,
    };
  }
  
  return null;
}

/**
 * Get the DEX name for a chain
 */
export function getDexName(chainId: number): 'PancakeSwap' | 'Uniswap' | 'DragonSwap' | 'TraderJoe' | 'Velodrome' | null {
  const contracts = DEX_CONTRACTS[chainId];
  return contracts?.dexName || null;
}

/**
 * Get the DEX version for a chain
 */
export function getDexVersion(chainId: number): 'V2' | 'V3' | null {
  const contracts = DEX_CONTRACTS[chainId];
  return contracts?.version || null;
}

/**
 * Get the native token symbol for a chain
 */
export function getNativeTokenSymbol(chainId: number): string {
  // BNB chains
  if (chainId === 204 || chainId === 5611) return 'BNB'; // opBNB
  if (chainId === 56 || chainId === 97) return 'BNB'; // BSC
  
  // ETH chains
  if (chainId === 1 || chainId === 11155111) return 'ETH'; // Ethereum
  if (chainId === 42161 || chainId === 421614) return 'ETH'; // Arbitrum
  if (chainId === 10 || chainId === 11155420) return 'ETH'; // Optimism
  if (chainId === 8453 || chainId === 84532) return 'ETH'; // Base
  if (chainId === 2741 || chainId === 11124) return 'ETH'; // Abstract
  
  // MATIC chain
  if (chainId === 137 || chainId === 80002) return 'MATIC'; // Polygon
  
  // AVAX chains
  if (chainId === 43114 || chainId === 43113) return 'AVAX'; // Avalanche
  
  // SEI chains
  if (chainId === 1329 || chainId === 1328) return 'SEI'; // Sei
  
  return 'ETH'; // Default
}

export interface DexOption {
  name: 'PancakeSwap' | 'Uniswap' | 'DragonSwap' | 'TraderJoe' | 'QuickSwap' | 'Velodrome';
  version: 'V2' | 'V3';
  label: string;
  logo: string;
}

/**
 * Get the logo path for a DEX
 */
function getDexLogo(dexName: 'PancakeSwap' | 'Uniswap' | 'DragonSwap' | 'TraderJoe' | 'QuickSwap' | 'Velodrome'): string {
  const logoMap: Record<string, string> = {
    'PancakeSwap': '/Pancakeswap logo MdbApp.png',
    'Uniswap': '/Uniswap Logo.png',
    'DragonSwap': '/DragonSwap Logo MdbApp.io.png',
    'TraderJoe': '/Trader Joe Logo MdbApp.png',
    'QuickSwap': '/QuickSwap Logo.png',
    'Velodrome': '/Veledrome V2.png'
  };
  return logoMap[dexName] || '/Uniswap Logo.png';
}

/**
 * Get all available DEXes for a given chain
 * Returns array of DEX options that can be used on this network
 */
export function getAvailableDexes(chainId: number): DexOption[] {
  const contracts = DEX_CONTRACTS[chainId];
  if (!contracts) return [];
  
  const dexes: DexOption[] = [];
  
  // Add V3 option if supported
  if (contracts.supportsV3 || contracts.version === 'V3') {
    dexes.push({
      name: contracts.dexName,
      version: 'V3',
      label: `${contracts.dexName} V3`,
      logo: getDexLogo(contracts.dexName)
    });
  }
  
  // Add V2 option if supported
  if (contracts.supportsV2 || contracts.version === 'V2') {
    // Special case for Polygon - QuickSwap V2
    if (chainId === 137 || chainId === 80002) {
      dexes.push({
        name: 'QuickSwap',
        version: 'V2',
        label: 'QuickSwap V2',
        logo: getDexLogo('QuickSwap')
      });
    }
    // Special case for Avalanche - Trader Joe V2
    else if (chainId === 43114 || chainId === 43113) {
      dexes.push({
        name: 'TraderJoe',
        version: 'V2',
        label: 'Trader Joe V2',
        logo: getDexLogo('TraderJoe')
      });
    }
    // Special case for Optimism - Velodrome V2
    else if (chainId === 10 || chainId === 11155420) {
      dexes.push({
        name: 'Velodrome',
        version: 'V2',
        label: 'Velodrome V2',
        logo: getDexLogo('Velodrome')
      });
    }
    // Default case - use the same DEX name
    else {
      dexes.push({
        name: contracts.dexName,
        version: 'V2',
        label: `${contracts.dexName} V2`,
        logo: getDexLogo(contracts.dexName)
      });
    }
  }
  
  return dexes;
}

/**
 * Check if a network has multiple DEX options
 */
export function hasMultipleDexOptions(chainId: number): boolean {
  return getAvailableDexes(chainId).length > 1;
}

// Backwards compatibility exports
export const PANCAKESWAP_V3_CONTRACTS = DEX_CONTRACTS;
export const PANCAKESWAP_V3_FEE_TIERS = V3_FEE_TIERS;
export const getPancakeSwapV3Contracts = getDexContracts;
export const isPancakeSwapV3Supported = isDexSupported;
